import {useContext, useEffect, useState} from 'react';
import {CourseContext} from './CourseContext';
import {TimeContext, useServerTime, ONE_WEEK_MS} from './TimeContext';
import {
  CourseWeekId,
  SectionWeekId,
  parseSectionWeekId,
  computeNextSectionSlot,
  getCurrentSectionWeek,
  type SectionClock,
} from '@cip/core/section';
import {localDaysBetween, parseLocalIsoDate} from '@cip/core/utils/time';
import { isNullOrUndefined } from 'utils/general';

// Section weeks start at 1, so that even during onboarding, any decisions like covers are registered to week 1
export const MIN_SECTION_WEEK = 1;
// Course weeks start at 0 for onboarding week
const MIN_COURSE_WEEK = 0;

export const useCourseLessonWeek = () => {
  const {_courseLessonWeek} = useContext(CourseContext);
  return _courseLessonWeek as CourseWeekId;
};

/**
 * Pure helper backing {@link useLocalCourseLessonWeek}. Exported for unit
 * tests; components should use the hook.
 *
 * Bucket semantics are 7-day windows over local-calendar dates: a `daysDiff`
 * in the closed interval `[0, 6]` is week 1, `[7, 13]` is week 2, and so on.
 * Diffs `< 0` (today's local date precedes the start date) fall through to
 * {@link MIN_COURSE_WEEK} (= 0, "pre-course").
 *
 * @param courseStartDate ISO 8601 course start (e.g.
 *   `"2026-01-05T00:00:00-08:00"`). Only the leading `YYYY-MM-DD` is read —
 *   any time and offset suffix is intentionally ignored so the cutover
 *   tracks the learner's local calendar rather than the course config's
 *   timezone.
 * @param now Optional anchor for "today". Defaults to `new Date()` (the
 *   caller's wall clock). Pass a fixed Date in tests to lock the result.
 * @return Course lesson week (1-indexed once `now`'s local date reaches the
 *   start). Returns 0 when `courseStartDate` is missing/unparseable, or
 *   when `now`'s local date is still before the start.
 */
export const computeLocalCourseLessonWeek = ({
  courseStartDate,
  now = new Date(),
}: {
  courseStartDate: string;
  now?: Date;
}): number => {
  const startLocal = parseLocalIsoDate(courseStartDate);
  if (!startLocal) return MIN_COURSE_WEEK;
  const daysDiff = localDaysBetween(startLocal, now);
  return Math.max(MIN_COURSE_WEEK, Math.floor(daysDiff / 7) + 1);
};

/**
 * Like {@link useCourseLessonWeek}, but anchored on the user's local-timezone
 * calendar date instead of UTC milliseconds.
 *
 * Reads only the `YYYY-MM-DD` prefix of `courseStartDate` and compares it to
 * the user's local "today". The week ticks over at local midnight: on the
 * start date itself the user sees week 1; exactly seven local-calendar days
 * later they see week 2, regardless of which timezone they're in.
 *
 * Use this for calendar UI surfaces where learners across timezones should
 * agree with their own clock about "which week is it right now". For logic
 * that has to align with section schedules or live course timing, keep using
 * {@link useCourseLessonWeek}.
 *
 * @return Course lesson week (1-indexed once the local date reaches start),
 *   or 0 ("pre-course") until `courseStartDate` and server time are known,
 *   or while the local date still precedes the start.
 */
export const useLocalCourseLessonWeek = (): CourseWeekId => {
  const {courseStartDate} = useContext(CourseContext);
  const {getServerTimeMs} = useContext(TimeContext);
  const serverNowMs = getServerTimeMs?.();
  // Server time, so a skewed device clock can't push a learner into the
  // wrong week, projected onto the user's local calendar.
  if (serverNowMs === undefined) {
    return MIN_COURSE_WEEK as CourseWeekId;
  }
  return computeLocalCourseLessonWeek({
    courseStartDate,
    now: new Date(serverNowMs),
  }) as CourseWeekId;
};

export const useCourseSectionWeek = () => {
  const {_courseSectionWeek} = useContext(CourseContext);
  return _courseSectionWeek as SectionWeekId;
};

export const useNCourseWeeks = () => {
  const {nCourseWeeks} = useContext(CourseContext);
  return nCourseWeeks as CourseWeekId;
}

const UPDATE_INTERVAL = 10000; // 10 seconds


/**
 * Returns the next section date and course week. A meeting remains current
 * over the half-open window `[start, start + grace)`, where grace is
 * `computeNextSectionSlot`'s default `graceMs`. While server time is unknown
 * the result is loading, with no date or week; it never falls back to the
 * device clock.
 * @param timeIndex
 * @returns
 */
export const useNextSectionDateAndWeek = (timeIndex: number | 'none' | null) => {
  const {
    isLoading: courseIsLoading,
    firstSectionTimestamp,
    sectionClock,
  } = useContext(CourseContext);
  const serverTime = useServerTime(10000);
  const isServerTimeKnown =
    typeof serverTime === 'number' && Number.isFinite(serverTime);

  if (isNullOrUndefined(timeIndex) || timeIndex === 'none') {
    return {
      nextSectionDate: null,
      nextSectionWeek: null,
      isLoading: false,
      error: null,
    };
  }

  if (courseIsLoading || !isServerTimeKnown) {
    return {
      nextSectionDate: null,
      nextSectionWeek: null,
      isLoading: true,
      error: null,
    };
  }

  if (!firstSectionTimestamp) {
    return {
      nextSectionDate: null,
      nextSectionWeek: null,
      isLoading: false,
      error: new Error('First section timestamp not defined'),
    };
  }

  try {
    // Delegate the "which week is next?" loop + one-hour grace window
    // to the shared `computeNextSectionSlot` helper so the same pure
    // logic is unit-tested under both TZ=UTC and TZ=Africa/Cairo in
    // `@cip/core/section/slotTime.test.ts`.
    const {nextSectionDate, sectionWeekIdx} = computeNextSectionSlot({
      firstSectionTimestamp,
      timeIndex,
      nowMs: new Date(serverTime).getTime(),
      clock: sectionClock,
    });
    const nextSectionWeek = parseSectionWeekId(sectionWeekIdx + 1);
    return {
      nextSectionDate,
      nextSectionWeek,
      isLoading: false,
      error: null,
    };
  } catch (e) {
    const err =
      e instanceof Error ? e : new Error(typeof e === 'string' ? e : String(e));
    return {
      nextSectionDate: null,
      nextSectionWeek: null,
      isLoading: false,
      error: err,
    };
  }
};

/**
 * Assumptions:
 * Definitions:
 * - courseLessonWeek - this is the week of the course that is defined by content. Week 1 starts on the first day of class (specified in the CourseContext).
 * - courseSectionWeek - This week is defined by sections. The week ends when the last section of the week ends. Week 1 of courseSectionWeek starts
 * the friday before the first section of the course.
 */


export const useCourseWeeksInternal = ({
  isLoading,
  firstSectionTimestamp,
  sectionTimeDelta,
  courseStartDate,
  sectionClock,
}: {
  isLoading: boolean;
  firstSectionTimestamp: string;
  sectionTimeDelta: number;
  courseStartDate: string;
  sectionClock: SectionClock;
}) => {
  // const serverTime = useServerTime(10000);
  // const { isLoading, firstSectionTimestamp, sectionTimeDelta, courseStartDate } = useContext(CourseContext);
  const [_courseLessonWeek, setCourseLessonWeek] = useState(undefined);
  const [_courseSectionWeek, setCourseSectionWeek] = useState(undefined);
  const { getServerTimeMs } = useContext(TimeContext);

  useEffect(() => {
    if (
      isLoading || !getServerTimeMs
    ) return;

    const updateWeeks = () => {
      const courseStartDateTime = new Date(courseStartDate);
      const nowMs = getServerTimeMs();
      if (nowMs === undefined) {
        return;
      }
      // subtract current date from course start date and take the delta (round to the nearest week)
      const newCurrentCourseWeek = Math.ceil(
        (nowMs - courseStartDateTime.getTime()) /
          ONE_WEEK_MS,
      );

      // Section week uses the shared formula from @cip/core
      const newCurrentSectionWeek = getCurrentSectionWeek({
        firstSectionTimestamp,
        sectionTimeDelta,
        nowMs,
        clock: sectionClock,
      });
      // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      let _safeCourseLessonWeek = Math.max(MIN_COURSE_WEEK, newCurrentCourseWeek);
      // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      let _safeSectionLessonWeek = Math.max(MIN_SECTION_WEEK, newCurrentSectionWeek);
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setCourseLessonWeek(_safeCourseLessonWeek as CourseWeekId);
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setCourseSectionWeek(_safeSectionLessonWeek as SectionWeekId);
    }
    updateWeeks();
    const interval = setInterval(() => {
      updateWeeks();
    }, UPDATE_INTERVAL);
    return () => clearInterval(interval);
  }, [isLoading, firstSectionTimestamp, sectionTimeDelta, courseStartDate, getServerTimeMs, sectionClock]);

  // Guarentee that the weeks are not negative
  // At min, 0
  // Any time before the course begins, we assume the week is 0

  return {
    _courseLessonWeek: _courseLessonWeek as CourseWeekId,
    _courseSectionWeek: _courseSectionWeek as SectionWeekId,
  };
};
